import React, { ReactNode, useEffect, KeyboardEvent, MouseEvent } from 'react';

import { Moment } from 'moment';
import CustomText from '../../../../Typography/CustomText/CustomText';

import { ShortcutButton, ShortcutHint, ShortcutList, Item } from '../styles';
import { defaultShortcuts } from '../Constants';

export type CustomDate = Date | Moment | string;

export interface Shortcut {
  label: ReactNode;
  hotkey?: string;
  date: CustomDate;
}

export interface CalendarShortcutsProps {
  /** Callback for closing the calendar */
  closeCalendar: () => void;
  /** Callback for clicking a date */
  onDateClick: (e: any | MouseEvent, date: CustomDate) => void;
  /** Override default's shortcuts */
  shortcuts?: Array<Shortcut>;
}

function CalendarShortcuts(props: CalendarShortcutsProps) {
  const { shortcuts = defaultShortcuts, onDateClick, closeCalendar, ...otherProps } = props;

  const handleKeyPress = (e: KeyboardEvent) => {
    const shortcut = shortcuts.filter(s => s.hotkey && s.hotkey.toUpperCase() === e.key.toUpperCase())[0];
    if (shortcut) {
      onDateClick(e, shortcut.date);
      closeCalendar();
    }
  };

  const handler = e => {
    if (e.target.tagName === 'SELECT') {
      e.preventDefault();
    }
    handleKeyPress(e);
  };

  useEffect(() => {
    window.addEventListener('keypress', handler);

    return () => {
      window.removeEventListener('keypress', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>, date: Date | Moment | string) => {
    onDateClick(e, date);
    closeCalendar();
  };

  return (
    <ShortcutList {...otherProps}>
      {shortcuts.map(shortcut => (
        <Item key={shortcut.hotkey}>
          <ShortcutButton
            onClick={e => handleClick(e, shortcut.date)}
            aria-label={`press ${shortcut.hotkey} for ${shortcut.date}`}
          >
            <CustomText display="inline">{shortcut.label}</CustomText>
            {shortcut.hotkey !== undefined && (
              <ShortcutHint>
                <CustomText size="xsmall" color="gray" display="inline">
                  Press&#160;
                  {shortcut.hotkey}
                </CustomText>
              </ShortcutHint>
            )}
          </ShortcutButton>
        </Item>
      ))}
    </ShortcutList>
  );
}

CalendarShortcuts.defaultProps = {
  shortcuts: defaultShortcuts,
};

CalendarShortcuts.displayName = 'Shortcuts';

export default CalendarShortcuts;
